import React, { useRef, useState, useEffect, useCallback } from "react";
import { OpenInNew, Code, Done } from '@material-ui/icons';
import {scaleLinear} from 'd3-scale';

import { getText } from '../languages';

import './style.scss';
import close from '../images/ic_close.svg';

const defaultEmbedWidth = 600;

export default function Export(props) {
  const { showExport, setShowExport, regionType, language, directionality } = props;

  const [stateCountyOptions, setStateCountyOptions] = useState(['state', 'county'])
  const [embedWidth, setEmbedWidth] = useState(defaultEmbedWidth)
  const iframeCodeRef = useRef()

  const clickIframeCode = () => {
    iframeCodeRef.current.focus()
    iframeCodeRef.current.select()
    document.execCommand("copy");
  }
  const embedType = regionType === 'county'
    ? 'stateCounty'
    : regionType === 'state'
      ? 'stateCounty'
      : regionType === 'usa'
        ? 'usa'
        : regionType === 'world'
          ? 'world'
          : regionType === 'country'
            ? 'country'
            : '';
  if (embedType === '') {
    console.error('invalid embed type')
  }

  let finalUrlEmbedType = embedType
  if (finalUrlEmbedType === 'stateCounty') {
    if (stateCountyOptions.length === 1) {
      finalUrlEmbedType = stateCountyOptions[0]
    }
  }

 // these are kinda hard coded based on testing out different sizes in dev tools
  const usaHeightScale = scaleLinear()
    .domain([400, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1700])
    .range([645, 735, 855, 955, 1080, 1175, 1285, 1495, 1720, 2040])
  const stateAndCountyHeightScale = scaleLinear()
    .domain([400, 500, 600, 700, 800, 801, 900, 1000, 1200, 1400, 1700])
    .range([1726, 1825, 2015, 2275, 2535, 885, 910, 935, 1030, 1160, 1355])
  const stateOrCountyHeightScale = scaleLinear()
    .domain([400, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1700])
    .range([884, 934, 1028, 1160, 1290, 1420, 1550, 1810, 2070, 2460])
  //
  const worldHeightScale = scaleLinear()
    .domain([400, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1700])
    .range([600, 705, 815, 920, 964, 1080, 1180, 1495, 1610, 1932])
  const countryHeightScale = scaleLinear()
    .domain([400, 500, 600, 700, 800, 801, 900, 1000, 1200, 1400, 1700])
    .range([686, 788, 890, 995, 1096, 615, 665, 715, 815, 915, 1065])
  //

  const calculateEmbedHeight = useCallback((width) => {
    if (finalUrlEmbedType === 'usa') {
      return usaHeightScale(width)
    } else if (finalUrlEmbedType === 'world') {
      return worldHeightScale(width);
    } else if (finalUrlEmbedType === 'country') {
      return countryHeightScale(width);
    } else if (finalUrlEmbedType === 'stateCounty') {
      return stateAndCountyHeightScale(width)
    } else if (finalUrlEmbedType === 'state' || finalUrlEmbedType === 'county') {
      return stateOrCountyHeightScale(width)
    }
  },[usaHeightScale, worldHeightScale, countryHeightScale, stateAndCountyHeightScale, stateOrCountyHeightScale, finalUrlEmbedType])
  const calculateEmbedWidth = (height) => {
    if (embedType === 'usa') {
      return usaHeightScale.invert(height)
    } else if (finalUrlEmbedType === 'world') {
      return worldHeightScale.invert(height);
    } else if (finalUrlEmbedType === 'country') {
      return countryHeightScale.invert(height);
    } else if (finalUrlEmbedType === 'stateCounty') {
      return stateAndCountyHeightScale.invert(height)
    } else if (finalUrlEmbedType === 'state' || finalUrlEmbedType === 'county' || finalUrlEmbedType === 'country') {
      return stateOrCountyHeightScale.invert(height)
    }
  }

  const [embedHeight, setEmbedHeight] = useState(calculateEmbedHeight(embedWidth))

  const changeWidth = (event) => {
    const w = +event.target.value
    setEmbedWidth(w)
    setEmbedHeight(calculateEmbedHeight(w))
  }
  const changeHeight = (event) => {
    const h = +event.target.value
    setEmbedHeight(h)
    setEmbedWidth(calculateEmbedWidth(h))
  }

  useEffect(() => {
    setEmbedHeight(calculateEmbedHeight(embedWidth))
  }, [stateCountyOptions, calculateEmbedHeight, embedWidth])

  const changeStateCountyOption = option => (event) => {
    const newOptions = [...stateCountyOptions]
    if (event.target.checked) {
      newOptions.push(option)
    } else {
      newOptions.splice(newOptions.indexOf(option), 1)
    }
    setStateCountyOptions(newOptions)
  }
  let languageEmbedParam = ''
  if (language !== 'en') {
    languageEmbedParam = `&l=${language}`
  }
  const embedUrl = `${document.location.origin}${document.location.pathname}?embed=${finalUrlEmbedType}${languageEmbedParam}${document.location.hash}`
  const iframeCode = `<iframe style="width: ${embedWidth.toFixed(0)}px; height: ${embedHeight.toFixed(0)}px" src="${embedUrl}" frameborder="0" scrolling="no" ></iframe>`
  return (
    <div className={`Export ${showExport ? 'active' : ''} ${regionType} ${directionality}`}>
      <div className='close' onClick={() => setShowExport(false)}>
        <img src={close} alt='close' />
      </div>
      <div className='content'>
        <div className='heading'>
          {getText(language, 'exportEmbed')}
        </div>
        <div className='option'>
          <div className='icon'>
            <OpenInNew />
          </div>
          <div className='details' style={{ width: '90%'}}>
            <div><a href={embedUrl} rel="noopener noreferrer" target='_blank'>{embedUrl}</a></div>
          </div>
        </div>
        <div className='option'>
          <div className='icon' style={{ opacity: 0 }}>
            <Code />
          </div>
          <div className='details'>
            <div>
              {getText(language, 'embedOnYourWebsite')}
            </div>
          </div>
        </div>
        <div className='option'>
          <div className='icon'>
            <Code />
          </div>
          <div className='details'>
            <div>
              <input ref={iframeCodeRef} onClick={clickIframeCode} className='iframeCode' type='text' value={iframeCode} readOnly />
            </div>
            {
              /* <div>
              <input type='radio' name='type' value='iframe' checked />
              <input type='radio' name='type' value='script' />
              <div className='help'>
                ?
              </div>
              </div> */
            }
          </div>
        </div>
        {
          embedType === 'stateCounty' ? (
            <div className='option'>
              <div className='icon' style={{ opacity: 0 }}>
                <Code />
              </div>
              <div className='details'>
                <span style={{ paddingRight: '10px' }} className={stateCountyOptions.length === 1 && stateCountyOptions[0] === 'state' ? 'disabled' : ''}>
                  State Map: <input type="checkbox" checked={stateCountyOptions.includes('state')} onChange={changeStateCountyOption('state')} />
                </span>
                <span className={stateCountyOptions.length === 1 && stateCountyOptions[0] === 'county' ? 'disabled' : ''}>
                  County Map: <input type="checkbox" checked={stateCountyOptions.includes('county')} onChange={changeStateCountyOption('county')} />
                </span>
              </div>
            </div>
          ) : null
        }
        <div className='option'>
            <div className='icon' style={{ opacity: 0 }}>
              <Code />
            </div>
            <div className='details'>
              {getText(language, 'width')} <input value={embedWidth.toFixed(0)} size={4} onChange={changeWidth} />  x {getText(language, 'height')} <input value={embedHeight.toFixed(0)} size={4} onChange={changeHeight} />
            </div>
        </div>
        <div className='option'>
          <div className='icon'>
            <Done />
          </div>
          <div className='details'>
            <button className='copyToClipboard' onClick={clickIframeCode}>
              {getText(language, 'copyToClipboard')}
            </button>
          </div>
        </div>
        {
          /* <div className='heading'>
          Download
          </div> */
        }
        {
          /* <div className='buttons'>
          <div className='button'>
            <button>Download HTML</button>
          </div>
          <div className='button'>
            <button>Download Image</button>
          </div>
          </div> */
        }
      </div>
    </div>
  );
}